import { type FC } from 'react';

import { Overview } from '@eneco/flows/components';
import { useFormatter } from '@eneco/i18n';
import { Text } from '@eneco/sparky';

import type { SummaryPersonalDetailsTextualData } from './summary-step.types';
import type { FlowConsumerContextEnergy } from '../../../context';

interface Props {
  textualData?: SummaryPersonalDetailsTextualData;
  flowContext: Pick<
    FlowConsumerContextEnergy,
    | 'initials'
    | 'firstName'
    | 'surnamePreposition'
    | 'surname'
    | 'birthdate'
    | 'phoneNumber'
    | 'emailAddress'
    | 'postalCode'
    | 'houseNumber'
    | 'houseNumberSuffix'
    | 'street'
    | 'city'
    | 'isMoving'
    | 'isPersonalDetailsEditProhibited'
  >;
  onEditPersonalDetails: () => void;
  onEditAddress: () => void;
  onEditMoving: () => void;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const SummaryPersonalDetailsOverview: FC<Props> = ({
  textualData,
  flowContext,
  onEditPersonalDetails,
  onEditAddress,
  onEditMoving,
  headingLevel,
}) => {
  const { address } = useFormatter();
  const {
    initials,
    firstName,
    surnamePreposition,
    surname,
    birthdate,
    phoneNumber,
    emailAddress,
    postalCode,
    houseNumber,
    houseNumberSuffix,
    street,
    city,
    isMoving,
    isPersonalDetailsEditProhibited,
  } = flowContext;

  const editLabel = textualData?.editDetailsLabel?.value ?? '';
  const displayName = `${firstName || initials} ${
    surnamePreposition ? surnamePreposition + ' ' : ''
  }${surname}`;

  return (
    <Overview>
      <Overview.Header headingLevel={headingLevel}>{textualData?.title?.value}</Overview.Header>

      <Overview.Details
        type="profile"
        title={textualData?.personalDetailsLabel?.value ?? ''}
        editButton={
          isPersonalDetailsEditProhibited
            ? undefined
            : {
                label: editLabel,
                onClick: onEditPersonalDetails,
              }
        }>
        <Text as="p">
          {textualData?.nameLabel?.value}: {displayName}
        </Text>
        {birthdate && (
          <Text as="p">
            {textualData?.birthDateLabel?.value}: {birthdate}
          </Text>
        )}
        <Text as="p">
          {textualData?.phoneNumberLabel?.value}: {phoneNumber}
        </Text>
        <Text as="p">
          {textualData?.emailAddressLabel?.value}: {emailAddress}
        </Text>
      </Overview.Details>

      <Overview.Details
        type="address"
        title={textualData?.addressDetailsLabel?.value ?? ''}
        editButton={
          isPersonalDetailsEditProhibited
            ? undefined
            : {
                label: editLabel,
                onClick: onEditAddress,
              }
        }>
        <Text as="p">{address.streetAddress({ street, houseNumber, houseNumberSuffix })}</Text>
        <Text as="p">{address.postalCodeCity({ postalCode, city })}</Text>
      </Overview.Details>

      {!isPersonalDetailsEditProhibited && (
        <Overview.Details
          type="situation"
          title={textualData?.situationDetailsLabel?.value ?? ''}
          editButton={{
            label: editLabel,
            onClick: onEditMoving,
          }}>
          <Text as="p">
            {isMoving ? textualData?.willBeMovingLabel?.value : textualData?.wontBeMovingLabel?.value}
          </Text>
        </Overview.Details>
      )}
    </Overview>
  );
};
